import { Injectable } from '@angular/core';
import { IChangedFieldDetail, IPayCategoryHistory } from '../pay-categories/pay-categories.type';
import { IPayScaleGlobalSettingsHistory, IPayScaleHistory } from '../pay-scales/pay-scales.types';
import { IChangesDisplayService } from './changes-display.types';
import { ITimeSheetHistory } from '../time-sheets/time-sheets.type';

/**
 * Contains methods to assist the display of history and future changes
 */
@Injectable({
	providedIn: 'root'
})
export class ChangesDisplayService implements IChangesDisplayService {
	public convertPayCategoryHistoryToChangedFieldDetail(
		payCategoryHistory: IPayCategoryHistory[]
	): IChangedFieldDetail[] {
		const result: IChangedFieldDetail[] = [];

		for (const item of payCategoryHistory) {
			for (const field of item.changedFields) {
				if (field.field === 'PayCategoryArchived') {
					field.oldValue = null;
					field.newValue = null;
				}

				if (field.field === 'PayCategoryNoteArchived') {
					field.newValue = null;
				}

				result.push({
					field: field.field,
					oldValue: field.oldValue,
					newValue: field.newValue,
					changeDate: item.changeDate,
					changedByUserId: item.changedByUserId,
					changedByUserDisplayName: item.changedByUserDisplayName,
					effectiveDate: item.effectiveDate
				});
			}
		}

		return result;
	}

	public convertPayScaleHistoryToChangedFieldDetail(payScaleHistory: IPayScaleHistory[]): IChangedFieldDetail[] {
		const result: IChangedFieldDetail[] = [];

		for (const item of payScaleHistory) {
			for (const field of item.changedFields) {
				let fieldDescription: string = null;
				let fieldName: string = field.field;
				let fieldNameIsNormalized: boolean = false;
				if (item.yearsOfService) {
					let s: string = 's';
					if (item.yearsOfService === 1) {
						s = '';
					}
					fieldDescription = ` for ${item.yearsOfService} year${s} of service`;
					if (fieldName === 'NewRecordCreated') {
						// a new years of service record entry was created
						fieldName = `${this.numberToOrdinal(item.yearsOfService)}`;
						fieldDescription = ' year of service entry was added';
						field.oldValue = null;
						field.newValue = null;
						fieldNameIsNormalized = true;
					}
				}

				result.push({
					field: fieldName,
					extraFieldDescription: fieldDescription,
					fieldNameIsNormalized: fieldNameIsNormalized,
					oldValue: field.oldValue,
					newValue: field.newValue,
					changeDate: item.changeDate,
					changedByUserId: item.changedByUserId,
					changedByUserDisplayName: item.changedByUserDisplayName,
					effectiveDate: item.effectiveDate
				});
			}
		}

		return result;
	}

	public convertPayScaleSettingHistoryToChangedFieldDetail(
		payScaleSettingHistory: IPayScaleGlobalSettingsHistory[]
	): IChangedFieldDetail[] {
		const result: IChangedFieldDetail[] = [];

		for (const item of payScaleSettingHistory) {
			for (const field of item.changedFields) {
				result.push({
					field: field.field,
					oldValue: field.oldValue,
					newValue: field.newValue,
					changeDate: item.changeDate,
					changedByUserId: item.changedByUserId,
					changedByUserDisplayName: item.changedByUserDisplayName,
					effectiveDate: item.effectiveDate
				});
			}
		}

		return result;
	}

	public convertTimeSheetHistoryToChangedFieldDetail(timeSheetHistory: ITimeSheetHistory[]): IChangedFieldDetail[] {
		const result: IChangedFieldDetail[] = [];

		for (const item of timeSheetHistory) {
			for (const field of item.changedFields) {
				result.push({
					field: field.field,
					oldValue: field.oldValue,
					newValue: field.newValue,
					changeDate: item.changeDate,
					changedByUserId: item.changedByUserId,
					changedByUserDisplayName: item.changedByUserDisplayName
				});
			}
		}

		return result;
	}

	private numberToOrdinal(val: number): string {
		const j: number = val % 10;
		const k: number = val % 100;
		if (j === 1 && k !== 11) {
			return `${val}st`;
		}
		if (j === 2 && k !== 12) {
			return `${val}nd`;
		}
		if (j === 3 && k !== 13) {
			return `${val}rd`;
		}

		return `${val}th`;
	}
}
